import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { DefaultRootState } from 'react-redux'

import { fetchAgencyCascadeSchedulingPlan } from 'syft-acp-core/store/agency-cascade-scheduling-plans/actions'

/**
 * Subscribes to one scheduling plan and exposes a request guarded against duplicate in-flight fetches.
 * The request refetches even when a plan is cached: filled counts and line states change as agencies
 * book workers, so a cached plan goes stale quickly. The cached plan stays available while it refreshes.
 */
export const useSchedulingPlan = (planId: number) => {
  const dispatch = useDispatch()
  const plan = useSelector(
    (state: DefaultRootState) =>
      state.agencyCascadeSchedulingPlans.entityDetail[planId] ??
      state.agencyCascadeSchedulingPlans.entityMap[planId],
  )
  const requestState = useSelector(
    (state: DefaultRootState) => state.agencyCascadeSchedulingPlans.requestStateByPlanId[planId],
  )
  const isRequestInFlight = requestState?.isLoading ?? false
  const requestPlan = useCallback(() => {
    if (!isRequestInFlight) dispatch(fetchAgencyCascadeSchedulingPlan({ planId }))
  }, [dispatch, isRequestInFlight, planId])

  return {
    plan,
    isLoading: !plan && (requestState?.isLoading ?? true),
    isRefreshing: !!plan && isRequestInFlight,
    loadError: requestState?.error,
    requestPlan,
  }
}
